import React, { useState, useEffect, useRef } from 'react';
import { IoSearch, IoClose, IoSchool, IoLocationOutline } from 'react-icons/io5';
import { useHighSchoolSuggestions } from '@/hooks/useHighSchoolSearch';
import { highSchoolSearchService, HighSchool } from '@/services/highSchoolSearchService';
import './SchoolSearch.css';

interface SchoolSearchProps {
  value?: HighSchool | null;
  onChange: (school: HighSchool | null) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

const SchoolSearch: React.FC<SchoolSearchProps> = ({
  value,
  onChange,
  placeholder = "Search for your high school...",
  disabled = false,
  error
}) => {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use the high school suggestions hook
  const { suggestions, isLoading } = useHighSchoolSuggestions(query, 8);

  // Update query when value changes
  useEffect(() => {
    if (value) {
      setQuery(highSchoolSearchService.formatSchoolDisplayName(value));
    } else {
      setQuery('');
    }
  }, [value]);

  // Show suggestions when available
  useEffect(() => {
    setShowSuggestions(suggestions.length > 0 && query.length >= 2);
  }, [suggestions, query]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setSelectedIndex(-1);

    // Clear existing selection if query changes
    if (value && newQuery !== highSchoolSearchService.formatSchoolDisplayName(value)) {
      onChange(null);
    }
  };

  // Handle school selection
  const handleSchoolSelect = async (suggestion: any) => {
    try {
      // Get full school details
      const fullSchool = await highSchoolSearchService.getHighSchoolById(suggestion.id);
      onChange(fullSchool);
      setQuery(highSchoolSearchService.formatSchoolDisplayName(fullSchool));
      setShowSuggestions(false);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error getting school details:', error);
      // Fallback to suggestion data
      const fallbackSchool: HighSchool = {
        id: suggestion.id,
        name: suggestion.name,
        city: suggestion.city,
        state: suggestion.state,
        enrollment: suggestion.enrollment
      };
      onChange(fallbackSchool);
      setQuery(highSchoolSearchService.formatSchoolDisplayName(fallbackSchool));
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  // Handle clear selection
  const handleClear = () => {
    onChange(null);
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSchoolSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Handle focus
  const handleFocus = () => {
    if (query.length >= 2 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="school-search-container" ref={containerRef}>
      <div className={`school-search-input-wrapper ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}>
        <IoSearch className="school-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="school-search-input"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        {(query || value) && !disabled && (
          <button
            type="button"
            className="school-search-clear"
            onClick={handleClear}
            title="Clear selection"
          >
            <IoClose />
          </button>
        )}
        {isLoading && (
          <div className="school-search-loading">
            <div className="school-search-spinner"></div>
          </div>
        )}
      </div>

      {error && (
        <span className="school-search-error">{error}</span>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="school-search-suggestions">
          {suggestions.map((school, index) => (
            <div
              key={school.id}
              className={`school-search-suggestion ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSchoolSelect(school)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="school-suggestion-content">
                <div className="school-suggestion-header">
                  <IoSchool className="school-suggestion-icon" />
                  <span className="school-suggestion-name">{school.name}</span>
                  <span className="school-suggestion-type">High School</span>
                </div>
                <div className="school-suggestion-details">
                  <IoLocationOutline className="school-suggestion-location-icon" />
                  <span className="school-suggestion-location">
                    {[school.city, school.state].filter(Boolean).join(', ')}
                  </span>
                  {school.enrollment && (
                    <span className="school-suggestion-enrollment">
                      {school.enrollment.toLocaleString()} students
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showSuggestions && suggestions.length === 0 && !isLoading && query.length >= 2 && (
        <div className="school-search-no-results">
          <p>No schools found for "{query}"</p>
          <p className="school-search-no-results-hint">
            Try searching with just the school name or city
          </p>
        </div>
      )}
    </div>
  );
};

export default SchoolSearch;